import type { CSSProperties } from 'react';
import { useCurrentFrame, useVideoConfig } from 'remotion';

const revealCount = (
  localFrame: number,
  fps: number,
  len: number,
  cps: number,
): number => {
  const over = (len / cps) * fps;
  if (over <= 0) return len;
  return Math.max(0, Math.min(len, Math.floor((localFrame / over) * len)));
};

const revealedText = (full: string, count: number): string => {
  const c = Math.max(0, Math.min(full.length, Math.floor(count)));
  return full.slice(0, c);
};

const useTypewriter = (
  full: string,
  options: { cps?: number; speed?: number; startFrame?: number } = {},
) => {
  const { cps = 20, speed = 1, startFrame = 0 } = options;
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const local = frame * speed - startFrame;
  const count = local <= 0 ? 0 : revealCount(local, fps, full.length, cps);
  return {
    text: revealedText(full, count),
    count,
    done: count >= full.length,
    typing: count > 0 && count < full.length,
  };
};

const caretBlinkOpacity = (
  frame: number,
  opts: { fps: number; blinkPerSecond: number; speed: number },
): number => {
  const cycles = opts.blinkPerSecond <= 0 ? 1 : opts.blinkPerSecond;
  const halfPeriod = opts.fps / cycles / 2;
  if (halfPeriod <= 0) return 1;
  return Math.floor((frame * opts.speed) / halfPeriod) % 2 === 0 ? 1 : 0;
};

type CaretProps = {
  color?: string;
  blink?: boolean;
  blinkPerSecond?: number;
  speed?: number;
  style?: CSSProperties;
};

const Caret: React.FC<CaretProps> = ({
  color = 'currentColor',
  blink = false,
  blinkPerSecond = 1,
  speed = 1,
  style,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();
  const opacity = blink
    ? caretBlinkOpacity(frame, { fps, blinkPerSecond, speed })
    : 1;

  return (
    <span
      style={{
        display: 'inline-block',
        flexShrink: 0,
        borderRadius: 0,
        background: color,
        opacity,
        ...style,
      }}
    />
  );
};

export interface TypewriterProps {
  text: string;
  cursor?: boolean;
  charsPerSecond?: number;
  speed?: number;
  fontSize?: number;
  color?: string;
  cursorColor?: string;
  fontWeight?: number;
  className?: string;
  style?: CSSProperties;
}

export const Typewriter: React.FC<TypewriterProps> = ({
  text,
  cursor = true,
  charsPerSecond = 22,
  speed = 1,
  fontSize = 48,
  color = '#171717',
  cursorColor = '#171717',
  fontWeight = 600,
  className,
  style,
}) => {
  const tw = useTypewriter(text, { cps: charsPerSecond, speed });

  return (
    <span
      className={className}
      style={{
        fontSize,
        fontWeight,
        color,
        letterSpacing: '-0.03em',
        fontFamily:
          'var(--font-geist-sans), -apple-system, BlinkMacSystemFont, sans-serif',
        whiteSpace: 'pre',
        ...style,
      }}
    >
      {tw.text}
      {cursor && (
        <Caret
          color={cursorColor}
          blink={!tw.typing}
          speed={speed}
          style={{
            width: '0.08em',
            height: '1em',
            marginLeft: '0.04em',
            verticalAlign: 'text-bottom',
          }}
        />
      )}
    </span>
  );
};
